import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const InteractiveLessonDemo = () => {
  const navigate = useNavigate();
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [genieResponse, setGenieResponse] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);

  // Lesson 1 context for AI teacher
  const lessonContext = `You are Geni, a friendly AI teacher robot genie helping children aged 7-15 learn about Artificial Intelligence. 
  This is Lesson 1: Introduction to AI. 
  You should explain concepts simply, use examples kids understand, encourage questions, and be enthusiastic and supportive.
  Keep responses under 30 seconds. Use simple language appropriate for children.`;

  useEffect(() => {
    // Connect to OpenAI Realtime API through our backend
    const connectWebSocket = () => {
      const wsUrl = `${process.env.REACT_APP_BACKEND_URL.replace('https', 'wss')}/api/openai-realtime/ws`;
      
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('Connected to voice AI');
        setConnectionStatus('connected');
        
        // Send lesson context
        wsRef.current.send(JSON.stringify({
          type: 'session.update',
          session: {
            instructions: lessonContext
          }
        }));
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle different message types
          if (data.type === 'conversation.item.created') {
            if (data.item.role === 'assistant') {
              setIsSpeaking(true);
            }
          } else if (data.type === 'response.audio_transcript.delta') {
            setGenieResponse(prev => prev + data.delta);
          } else if (data.type === 'response.done') {
            setIsSpeaking(false);
          } else if (data.type === 'conversation.item.input_audio_transcription.completed') {
            setTranscript(data.transcript);
          }
        } catch (error) {
          console.error('WebSocket message error:', error);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
      };

      wsRef.current.onclose = () => {
        console.log('WebSocket closed');
        setConnectionStatus('disconnected');
      };
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const startListening = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      audioContextRef.current = new AudioContext({ sampleRate: 24000 });
      const source = audioContextRef.current.createMediaStreamSource(stream);
      const processor = audioContextRef.current.createScriptProcessor(4096, 1, 1);

      source.connect(processor);
      processor.connect(audioContextRef.current.destination);

      processor.onaudioprocess = (e) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && isListening) {
          const inputData = e.inputBuffer.getChannelData(0);
          const pcm16 = new Int16Array(inputData.length);
          
          for (let i = 0; i < inputData.length; i++) {
            pcm16[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
          }

          // Send audio to backend
          wsRef.current.send(JSON.stringify({
            type: 'input_audio_buffer.append',
            audio: btoa(String.fromCharCode(...new Uint8Array(pcm16.buffer)))
          }));
        }
      };

      setIsListening(true);
      setTranscript('');
      setGenieResponse('');
      
    } catch (error) {
      console.error('Microphone error:', error);
      alert('Please allow microphone access to talk with Geni!');
    }
  };

  const stopListening = () => {
    setIsListening(false);
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      // Commit audio and request response
      wsRef.current.send(JSON.stringify({
        type: 'input_audio_buffer.commit'
      }));
      
      wsRef.current.send(JSON.stringify({
        type: 'response.create'
      }));
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1628 0%, #1a2f4a 100%)',
      padding: '20px',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated particles */}
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: '4px',
            height: '4px',
            background: i % 2 === 0 ? '#ffd700' : '#00d9ff',
            borderRadius: '50%',
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animation: `twinkle ${2 + Math.random() * 3}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 2}s`,
            opacity: 0
          }}
        />
      ))}

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        position: 'relative',
        zIndex: 1
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255,255,255,0.2)',
              padding: '12px 24px',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>

          <div style={{ textAlign: 'center' }}>
            <h1 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '8px',
              textShadow: '0 0 20px rgba(0,217,255,0.5)'
            }}>
              🎓 Interactive AI Lesson with Geni
            </h1>
            <p style={{
              fontSize: '16px',
              color: 'rgba(255,255,255,0.8)'
            }}>
              Lesson 1: What Is Artificial Intelligence?
            </p>
          </div>

          <div style={{
            padding: '12px 24px',
            background: connectionStatus === 'connected' ? 'rgba(72,187,120,0.2)' : 'rgba(245,101,101,0.2)',
            border: `2px solid ${connectionStatus === 'connected' ? '#48bb78' : '#f56565'}`,
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            {connectionStatus === 'connected' ? '🟢 Connected' : '🔴 Connecting...'}
          </div>
        </div>

        {/* Main Content */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '500px 1fr',
          gap: '30px',
          alignItems: 'start'
        }}>
          {/* Genie Side */}
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(20px)',
            border: '2px solid rgba(255,255,255,0.1)',
            borderRadius: '20px',
            padding: '40px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            position: 'sticky',
            top: '20px'
          }}>
            {/* Genie's speech bubble */}
            {(genieResponse || isSpeaking) && (
              <div style={{
                background: 'rgba(255,255,255,0.95)',
                border: '3px solid #00d9ff',
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '30px',
                boxShadow: '0 0 20px rgba(0,217,255,0.3)',
                animation: 'fadeInDown 0.3s ease-out',
                position: 'relative'
              }}>
                <div style={{
                  fontSize: '16px',
                  color: '#2d3748',
                  lineHeight: '1.6',
                  minHeight: '60px'
                }}>
                  {genieResponse || 'Listening and thinking...'}
                </div>
                <div style={{
                  position: 'absolute',
                  bottom: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '0',
                  height: '0',
                  borderLeft: '12px solid transparent',
                  borderRight: '12px solid transparent',
                  borderTop: '12px solid #00d9ff'
                }}></div>
              </div>
            )}

            {/* Animated Genie */}
            <div style={{
              position: 'relative',
              width: '100%',
              height: '400px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '30px'
            }}>
              {/* Glow effect */}
              {(isSpeaking || isListening) && (
                <div style={{
                  position: 'absolute',
                  inset: '-30px',
                  background: `radial-gradient(circle, ${isListening ? 'rgba(255,215,0,0.3)' : 'rgba(0,217,255,0.3)'} 0%, transparent 70%)`,
                  borderRadius: '50%',
                  animation: 'pulse 2s ease-in-out infinite',
                  filter: 'blur(30px)'
                }}></div>
              )}

              {/* Genie Image */}
              <img
                src="/images/genie-teacher.png"
                alt="Geni AI Teacher"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  filter: (isSpeaking || isListening) ? 'drop-shadow(0 0 30px rgba(0,217,255,0.8))' : 'drop-shadow(0 10px 20px rgba(0,0,0,0.5))',
                  animation: 'floatGenie 3s ease-in-out infinite',
                  position: 'relative',
                  zIndex: 2
                }}
              />

              {/* Magical particles around genie */}
              {(isSpeaking || isListening) && (
                <>
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      style={{
                        position: 'absolute',
                        width: '8px',
                        height: '8px',
                        background: i % 2 === 0 ? '#ffd700' : '#00d9ff',
                        borderRadius: '50%',
                        top: '50%',
                        left: '50%',
                        animation: `orbit${i} 3s linear infinite`,
                        boxShadow: `0 0 10px ${i % 2 === 0 ? '#ffd700' : '#00d9ff'}`
                      }}
                    />
                  ))}
                </>
              )}
            </div>

            {/* Status */}
            <div style={{
              textAlign: 'center',
              fontSize: '18px',
              fontWeight: '600',
              color: 'white',
              marginBottom: '20px',
              minHeight: '30px'
            }}>
              {isListening && '🎤 I\'m listening to you...'}
              {isSpeaking && '💬 Geni is speaking...'}
              {!isListening && !isSpeaking && '👂 Press and hold to speak with Geni'}
            </div>

            {/* Push to Talk Button */}
            <button
              onMouseDown={startListening}
              onMouseUp={stopListening}
              onTouchStart={startListening}
              onTouchEnd={stopListening}
              disabled={connectionStatus !== 'connected'}
              style={{
                width: '100%',
                padding: '20px',
                background: isListening 
                  ? 'linear-gradient(135deg, #f56565 0%, #ed8936 100%)'
                  : 'linear-gradient(135deg, #00d9ff 0%, #0099cc 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                fontSize: '20px',
                fontWeight: 'bold',
                cursor: connectionStatus !== 'connected' ? 'not-allowed' : 'pointer',
                boxShadow: isListening ? '0 0 30px rgba(245,101,101,0.6)' : '0 8px 20px rgba(0,217,255,0.4)',
                transform: isListening ? 'scale(0.98)' : 'scale(1)',
                transition: 'all 0.2s',
                opacity: connectionStatus !== 'connected' ? 0.5 : 1
              }}
            >
              {isListening ? '🎙️ Release to Send' : '🎤 Hold to Speak'}
            </button>

            <p style={{
              textAlign: 'center',
              color: 'rgba(255,255,255,0.6)',
              fontSize: '14px',
              marginTop: '12px'
            }}>
              Hold the button and speak your question
            </p>
          </div>

          {/* Conversation & Lesson Content Side */}
          <div style={{
            background: 'rgba(255,255,255,0.05)',
            backdropFilter: 'blur(20px)',
            border: '2px solid rgba(255,255,255,0.1)',
            borderRadius: '20px',
            padding: '40px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            minHeight: '600px'
          }}>
            <h2 style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '24px',
              borderBottom: '3px solid #00d9ff',
              paddingBottom: '12px'
            }}>
              💬 Conversation with Geni
            </h2>

            {/* Conversation History */}
            <div style={{
              background: 'rgba(0,0,0,0.3)',
              borderRadius: '12px',
              padding: '20px',
              minHeight: '300px',
              maxHeight: '400px',
              overflowY: 'auto',
              marginBottom: '30px'
            }}>
              {transcript && (
                <div style={{
                  background: 'rgba(255,255,255,0.1)',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  marginBottom: '12px',
                  borderLeft: '4px solid #ffd700'
                }}>
                  <div style={{ fontSize: '12px', color: '#ffd700', marginBottom: '4px', fontWeight: '600' }}>
                    You:
                  </div>
                  <div style={{ color: 'white', fontSize: '14px' }}>
                    {transcript}
                  </div>
                </div>
              )}

              {genieResponse && (
                <div style={{
                  background: 'rgba(0,217,255,0.1)',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  borderLeft: '4px solid #00d9ff'
                }}>
                  <div style={{ fontSize: '12px', color: '#00d9ff', marginBottom: '4px', fontWeight: '600' }}>
                    Geni:
                  </div>
                  <div style={{ color: 'white', fontSize: '14px' }}>
                    {genieResponse}
                  </div>
                </div>
              )}

              {!transcript && !genieResponse && (
                <div style={{
                  textAlign: 'center',
                  color: 'rgba(255,255,255,0.5)',
                  padding: '40px 20px',
                  fontSize: '16px'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
                  Start talking with Geni to begin your lesson!
                  <br /><br />
                  Try asking: "What is AI?" or "How does AI learn?"
                </div>
              )}
            </div>

            {/* Lesson Guide */}
            <div style={{
              background: 'rgba(0,217,255,0.1)',
              border: '2px solid rgba(0,217,255,0.3)',
              borderRadius: '12px',
              padding: '20px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#00d9ff',
                marginBottom: '12px'
              }}>
                📚 Lesson 1 Topics to Explore:
              </h3>
              <ul style={{
                color: 'rgba(255,255,255,0.9)',
                lineHeight: '2',
                paddingLeft: '20px'
              }}>
                <li>What is Artificial Intelligence?</li>
                <li>Where do we find AI in everyday life?</li>
                <li>How does AI learn from examples?</li>
                <li>What can AI do vs. what can't it do?</li>
                <li>The difference between AI and regular computers</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes floatGenie {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(2deg);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 0.4;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }

        @keyframes twinkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        ${[...Array(8)].map((_, i) => `
          @keyframes orbit${i} {
            from {
              transform: rotate(${i * 45}deg) translateX(150px) rotate(-${i * 45}deg);
            }
            to {
              transform: rotate(${i * 45 + 360}deg) translateX(150px) rotate(-${i * 45 + 360}deg);
            }
          }
        `).join('\n')}
      `}</style>
    </div>
  );
};

export default InteractiveLessonDemo;
